/**
 * หน้าจอ "ข้อยกเว้นการรับของ" (Receiving Exceptions, D-168 B6) — คิวที่จัดซื้อ
 * ทำงานจากของขาด/เกิน/ส่งช้าหลังปิด และรายงานสรุปมูลค่า
 *
 * สองแท็บที่อ่านจาก endpoint คนละเส้น: **คิว** (`GET /v1/receiving-exceptions`,
 * เปิดแบบไม่ปิดเป็นค่าเริ่มต้น โหลดเพิ่มด้วย cursor) และ **รายงานสรุป**
 * (`GET /v1/receiving-exceptions/summary`, ช่วงวันที่เริ่มต้น 90 วัน) อนุมัติไม่ได้
 * อยู่ในสองเส้นนี้เลย — เป็นเส้นเดิมของ D-091
 * (`POST /v1/receiving-discrepancies/{id}/approve`) ที่มี grant ของตัวเอง
 * (`INVENTORY` + `APPROVE` ไม่ใช่ `PROCUREMENT` + `VIEW` ที่เปิดหน้าจอนี้)
 *
 * โครงยืมมาจาก `GoodsReceiptListView` (ช่องกรองบนสุด + โหลดเพิ่มด้วย cursor) และ
 * `PurchaseOrderScreen` (ปุ่ม lifecycle รายแถวที่ผลสำเร็จ/ล้มเหลวอยู่ที่แถวนั้น) —
 * สิ่งที่ต่างออกไปคือ **อนุมัติสำเร็จแก้แถวในหน่วยความจำ ไม่โหลดทั้งตารางใหม่**
 * (invariant ของ WO นี้): แถวที่ยังอยู่ในคิว (`include_closed=false`) หายไปทันทีที่
 * สถานะไม่ใช่ `OPEN`/`PENDING_MANAGEMENT_APPROVAL` อีกแล้ว ส่วนแถวที่ขอรวมที่ปิดแล้ว
 * มาด้วย (`include_closed=true`) ยังอยู่แต่เปลี่ยนสถานะ/ไม่มีปุ่มอนุมัติให้กดอีก
 *
 * การ gate สิทธิ์: `PROCUREMENT` + **VIEW** เป็นสิทธิ์ของการเปิดหน้าจอนี้ (ด่านอยู่ที่
 * route — `DesktopModulePage` ตรวจก่อนเรนเดอร์ส่วนนี้แล้ว) ส่วนปุ่ม "อนุมัติ" ต้องการ
 * `INVENTORY` + **APPROVE** ซึ่งเป็นคนละโมดูลโดยเจตนา (ดูคำอธิบายข้างบน) ถูกบังคับ
 * ทั้งที่การแสดงปุ่ม (`canApprove`) และซ้ำอีกครั้งฝั่งเซิร์ฟเวอร์เสมอ
 *
 * ⚠️ ทั้งหมดนี้เป็น UX layer ไม่ใช่ด่านความปลอดภัย เซิร์ฟเวอร์เป็นผู้ตัดสินทุก request
 * เสมอ (`references/authorization-rbac.md` ข้อ 5)
 */

import { useEffect, useState, type FormEvent } from 'react';

import { StatusBadge } from '../StatusBadge';
import { InfoHint } from '../components/InfoHint';
import type { PermissionsState } from '../../lib/permissions/types';
import {
  approveReceivingDiscrepancy,
  currentStatusFromFailure,
  getReceivingExceptionSummary,
  listReceivingExceptions,
  ruleFromFailure,
  type ReceivingExceptionFailure,
} from '../../lib/receivingExceptions/client';
import {
  canApprove,
  defaultSummaryDateRange,
  discrepancyStatusLabel,
  discrepancyStatusTone,
  discrepancyTypeLabel,
  discrepancyTypeTone,
  isApprovableStatus,
  isRequestableSummaryRange,
  reasonCodeLabel,
  sourceLabel,
  valueLabel,
} from '../../lib/receivingExceptions/receivingExceptionView';
import {
  APPROVER_MUST_DIFFER_FROM_RAISER,
  RECEIVING_DISCREPANCY_NOT_APPROVABLE,
  RECEIVING_EXCEPTIONS_RANGE_IS_REVERSED,
  RECEIVING_EXCEPTIONS_RANGE_TOO_WIDE,
  SOD_REOPENER_CANNOT_APPROVE,
  SUMMARY_MAX_RANGE_DAYS,
  type ReceivingExceptionItem,
  type ReceivingExceptionSourceFilter,
  type ReceivingExceptionSummary,
  type ReceivingExceptionTypeFilter,
} from '../../lib/receivingExceptions/types';
import { PROCUREMENT_MODULE } from '../../lib/procurement/types';

/* ------------------------------------------------- หน้าจอความล้มเหลว ----- */

type FailureContext = 'queue' | 'summary' | 'approve';

interface FailureCopy {
  readonly heading: string;
  readonly text: string;
}

function ruleCopy(rule: string | null, currentStatus: string | null): FailureCopy | null {
  switch (rule) {
    case RECEIVING_EXCEPTIONS_RANGE_TOO_WIDE:
      return {
        heading: 'ช่วงวันที่กว้างเกินไป',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${RECEIVING_EXCEPTIONS_RANGE_TOO_WIDE} — รายงานสรุปอ่านได้ไม่เกิน ${SUMMARY_MAX_RANGE_DAYS} วันต่อคำขอ แคบช่วงวันที่แล้วลองใหม่`,
      };
    case RECEIVING_EXCEPTIONS_RANGE_IS_REVERSED:
      return {
        heading: 'ช่วงวันที่กลับด้าน',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${RECEIVING_EXCEPTIONS_RANGE_IS_REVERSED} — วันสิ้นสุดมาก่อนวันเริ่มต้น`,
      };
    case APPROVER_MUST_DIFFER_FROM_RAISER:
      return {
        heading: 'ผู้อนุมัติต้องไม่ใช่ผู้บันทึกรายการนี้เอง',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${APPROVER_MUST_DIFFER_FROM_RAISER} — **ไม่ใช่เรื่องสิทธิ์ไม่พอ** คุณถือ INVENTORY + APPROVE ครบแล้ว แต่เป็นผู้บันทึกแถวนี้เอง ต้องให้เพื่อนร่วมงานอีกคนกดอนุมัติ แถวนี้ยังอยู่ในสถานะเดิม`,
      };
    case SOD_REOPENER_CANNOT_APPROVE:
      return {
        heading: 'ผู้ที่เปิด PO กลับไม่สามารถอนุมัติรอบนี้ได้',
        text: `เซิร์ฟเวอร์ตอบ 422 พร้อมกฎ ${SOD_REOPENER_CANNOT_APPROVE} — คุณเป็นผู้แก้ไข PO ของรอบที่ short-close ถูกเปิดกลับ การแบ่งแยกหน้าที่จึงให้คนอื่นเป็นผู้อนุมัติ แถวนี้ยังอยู่ในสถานะเดิม`,
      };
    case RECEIVING_DISCREPANCY_NOT_APPROVABLE:
      return {
        heading: 'แถวนี้ไม่อยู่ในสถานะที่อนุมัติได้แล้ว',
        text: `เซิร์ฟเวอร์ตอบ 409 พร้อมกฎ ${RECEIVING_DISCREPANCY_NOT_APPROVABLE} —${
          currentStatus === null ? '' : ` ขณะนี้แถวนี้อยู่ที่ ${discrepancyStatusLabel(currentStatus)}`
        } มีคนอนุมัติ/ปฏิเสธไปแล้ว โหลดคิวใหม่เพื่อดูสถานะจริง`,
      };
    default:
      return null;
  }
}

function failureCopy(
  failure: ReceivingExceptionFailure,
  context: FailureContext,
): FailureCopy {
  const currentStatus = currentStatusFromFailure(failure);
  switch (failure.status) {
    case 'unauthenticated':
      return {
        heading: 'ต้องเข้าสู่ระบบก่อนจึงจะใช้หน้าจอนี้ได้',
        text: 'เซิร์ฟเวอร์ตอบ 401 — เซสชันหมดอายุหรือถูกยกเลิกไปแล้ว',
      };
    case 'forbidden':
      return (
        ruleCopy(failure.error.rule, currentStatus) ?? {
          heading: 'สิทธิ์ชุดนี้ไม่ครอบคลุมคำขอนี้',
          text:
            context === 'approve'
              ? 'เซิร์ฟเวอร์ตอบ 403 — การอนุมัติต้องการ INVENTORY + APPROVE'
              : `เซิร์ฟเวอร์ตอบ 403 — คำขอนี้ต้องการโมดูล ${PROCUREMENT_MODULE}`,
        }
      );
    case 'not-found':
      return {
        heading: 'ไม่พบรายการที่คำขอนี้อ้างถึง',
        text: 'เซิร์ฟเวอร์ตอบ 404 — แถวนี้ไม่มีอยู่แล้ว หรืออยู่ในโรงงานนอกขอบเขตของคุณ',
      };
    case 'conflict':
      return (
        ruleCopy(failure.error.rule, currentStatus) ?? {
          heading: 'มีของที่ชนกันอยู่แล้วในระบบ',
          text: 'เซิร์ฟเวอร์ตอบ 409 — สิ่งที่ส่งไปชนกับสถานะที่เก็บอยู่จริง',
        }
      );
    case 'rule-violation':
      return (
        ruleCopy(failure.error.rule, currentStatus) ?? {
          heading: 'คำขอนี้ติดกฎทางธุรกิจ',
          text: 'เซิร์ฟเวอร์ตอบ 422 พร้อมรหัสกฎที่ชน',
        }
      );
    case 'bad-request':
      return {
        heading: 'คำขอนี้ถูกปฏิเสธว่าไม่ถูกต้อง',
        text: 'เซิร์ฟเวอร์ตอบ 400 — ข้อมูลที่ส่งไปไม่ตรงรูปแบบที่ API รับ',
      };
    case 'server-error':
      return {
        heading: `เซิร์ฟเวอร์ตอบ ${failure.httpStatus}`,
        text: 'คำขอไปถึงเซิร์ฟเวอร์แล้วแต่ทำงานไม่สำเร็จ',
      };
    case 'network-error':
      return {
        heading: 'ติดต่อเซิร์ฟเวอร์ไม่ได้',
        text: failure.detail,
      };
    case 'invalid-response':
      return {
        heading: 'ข้อมูลจากเซิร์ฟเวอร์ผิดรูปแบบ',
        text: 'หน้าจอปฏิเสธที่จะแสดงข้อมูลที่อ่านได้ไม่ครบ',
      };
    case 'cancelled':
      return {
        heading: 'ยกเลิกการเรียกข้อมูลแล้ว',
        text: 'คำขอถูกยกเลิกก่อนได้รับคำตอบ',
      };
  }
}

interface FailureNoticeProps {
  readonly failure: ReceivingExceptionFailure;
  readonly context: FailureContext;
  readonly testId: string;
  readonly onRetry?: () => void;
}

function ReceivingExceptionFailureNotice({
  failure,
  context,
  testId,
  onRetry,
}: FailureNoticeProps) {
  const copy = failureCopy(failure, context);
  const rule = ruleFromFailure(failure);
  const refusedByRule = failure.status === 'rule-violation' || failure.status === 'conflict';

  return (
    <div className="cap-notice" data-testid={testId} data-failure={failure.status} role="status">
      <StatusBadge tone={refusedByRule ? 'warning' : 'danger'}>
        {refusedByRule ? 'ติดกฎทางธุรกิจ' : 'ทำรายการไม่สำเร็จ'}
      </StatusBadge>
      <h3 className="cap-notice__title">{copy.heading}</h3>
      <p className="cap-notice__text">{copy.text}</p>
      {rule === null ? null : (
        <p className="cap-notice__text">
          กฎที่ชน: <code className="cap-table__code">{rule}</code>
        </p>
      )}
      {onRetry === undefined ? null : (
        <div className="cap-notice__actions">
          <button type="button" className="cap-button cap-button--ghost" onClick={onRetry}>
            ลองอีกครั้ง
          </button>
        </div>
      )}
    </div>
  );
}

/* --------------------------------------------------------------- หน้าจอ -- */

export interface ReceivingExceptionScreenProps {
  readonly permissions: PermissionsState;
  readonly apiBaseUrl: string;
  /** inject ได้เพื่อการทดสอบ — production ใช้ `fetch` ของเบราว์เซอร์ */
  readonly fetchImpl?: typeof fetch;
}

type Tab = 'queue' | 'summary';

type QueueState =
  | { readonly status: 'loading' }
  | {
      readonly status: 'ready';
      readonly items: readonly ReceivingExceptionItem[];
      readonly nextCursor: string | null;
      readonly loadingMore: boolean;
      readonly loadMoreFailure: ReceivingExceptionFailure | null;
    }
  | ReceivingExceptionFailure;

type SummaryState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly summary: ReceivingExceptionSummary }
  | ReceivingExceptionFailure;

const TYPE_FILTER_VALUES: readonly ReceivingExceptionTypeFilter[] = [
  'SHORT',
  'OVER',
  'LATE_DELIVERY',
];
const SOURCE_FILTER_VALUES: readonly ReceivingExceptionSourceFilter[] = [
  'ERP_DESK',
  'INTEGRATION',
];

export function ReceivingExceptionScreen({
  permissions,
  apiBaseUrl,
  fetchImpl,
}: ReceivingExceptionScreenProps) {
  const [tab, setTab] = useState<Tab>('queue');
  const userCanApprove = canApprove(permissions);

  /* ------------------------------------------------------------- คิว -- */

  const [discrepancyType, setDiscrepancyType] = useState<'' | ReceivingExceptionTypeFilter>('');
  const [source, setSource] = useState<'' | ReceivingExceptionSourceFilter>('');
  const [includeClosed, setIncludeClosed] = useState(false);
  const [supplierDraft, setSupplierDraft] = useState('');
  const [dateFromDraft, setDateFromDraft] = useState('');
  const [dateToDraft, setDateToDraft] = useState('');
  const [appliedSupplier, setAppliedSupplier] = useState('');
  const [appliedDateFrom, setAppliedDateFrom] = useState('');
  const [appliedDateTo, setAppliedDateTo] = useState('');
  const [reloadToken, setReloadToken] = useState(0);
  const [queue, setQueue] = useState<QueueState>({ status: 'loading' });

  const [approving, setApproving] = useState<string | null>(null);
  const [approveFailures, setApproveFailures] = useState<
    Readonly<Record<string, ReceivingExceptionFailure>>
  >({});

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    setQueue((previous) => (previous.status === 'ready' ? previous : { status: 'loading' }));

    void listReceivingExceptions(
      {
        include_closed: includeClosed,
        ...(discrepancyType === '' ? {} : { discrepancy_type: discrepancyType }),
        ...(source === '' ? {} : { source }),
        ...(appliedSupplier === '' ? {} : { supplier_code: appliedSupplier }),
        ...(appliedDateFrom === '' ? {} : { date_from: appliedDateFrom }),
        ...(appliedDateTo === '' ? {} : { date_to: appliedDateTo }),
      },
      { apiBaseUrl, fetchImpl, signal: controller.signal },
    ).then((result) => {
      if (!active || result.status === 'cancelled') {
        return;
      }
      setQueue(
        result.status === 'ready'
          ? {
              status: 'ready',
              items: result.page.items,
              nextCursor: result.page.next_cursor,
              loadingMore: false,
              loadMoreFailure: null,
            }
          : result,
      );
    });

    return () => {
      active = false;
      controller.abort();
    };
  }, [
    apiBaseUrl,
    fetchImpl,
    discrepancyType,
    source,
    includeClosed,
    appliedSupplier,
    appliedDateFrom,
    appliedDateTo,
    reloadToken,
  ]);

  const applyQueueFilters = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setAppliedSupplier(supplierDraft.trim());
    setAppliedDateFrom(dateFromDraft.trim());
    setAppliedDateTo(dateToDraft.trim());
  };

  const loadMore = async () => {
    if (queue.status !== 'ready' || queue.loadingMore || queue.nextCursor === null) {
      return;
    }
    const loaded = queue;
    setQueue({ ...loaded, loadingMore: true, loadMoreFailure: null });

    const result = await listReceivingExceptions(
      {
        include_closed: includeClosed,
        ...(discrepancyType === '' ? {} : { discrepancy_type: discrepancyType }),
        ...(source === '' ? {} : { source }),
        ...(appliedSupplier === '' ? {} : { supplier_code: appliedSupplier }),
        ...(appliedDateFrom === '' ? {} : { date_from: appliedDateFrom }),
        ...(appliedDateTo === '' ? {} : { date_to: appliedDateTo }),
        cursor: loaded.nextCursor ?? undefined,
      },
      { apiBaseUrl, fetchImpl },
    );
    if (result.status === 'cancelled') {
      return;
    }
    setQueue(
      result.status === 'ready'
        ? {
            status: 'ready',
            items: [...loaded.items, ...result.page.items],
            nextCursor: result.page.next_cursor,
            loadingMore: false,
            loadMoreFailure: null,
          }
        : { ...loaded, loadingMore: false, loadMoreFailure: result },
    );
  };

  /**
   * อนุมัติแถวหนึ่ง — สำเร็จแล้ว **แก้แถวในหน่วยความจำ ไม่โหลดทั้งตารางใหม่**
   * (invariant ของ WO นี้) แถวที่คิวนี้ไม่รวมที่ปิดแล้ว (`include_closed=false`)
   * หายไปทันทีที่สถานะไม่ใช่ `OPEN`/`PENDING_MANAGEMENT_APPROVAL` อีกแล้ว
   */
  const approve = async (item: ReceivingExceptionItem) => {
    if (approving !== null || !userCanApprove || !isApprovableStatus(item.status)) {
      return;
    }
    setApproving(item.discrepancy_id);
    setApproveFailures((previous) => {
      const next = { ...previous };
      delete next[item.discrepancy_id];
      return next;
    });

    const result = await approveReceivingDiscrepancy(item.discrepancy_id, {
      apiBaseUrl,
      fetchImpl,
    });

    setApproving(null);
    if (result.status === 'cancelled') {
      return;
    }
    if (result.status !== 'ready') {
      setApproveFailures((previous) => ({ ...previous, [item.discrepancy_id]: result }));
      return;
    }

    setQueue((previous) => {
      if (previous.status !== 'ready') {
        return previous;
      }
      const patched = previous.items.map((row) =>
        row.discrepancy_id === item.discrepancy_id
          ? {
              ...row,
              status: result.discrepancy.status,
              approved_by_employee_id: result.discrepancy.approved_by_employee_id,
              approved_at: result.discrepancy.approved_at,
            }
          : row,
      );
      return {
        ...previous,
        items: includeClosed
          ? patched
          : patched.filter((row) => row.discrepancy_id !== item.discrepancy_id),
      };
    });
  };

  /* --------------------------------------------------------- สรุป -- */

  const [initialSummaryRange] = useState(() => defaultSummaryDateRange());
  const [summaryDateFrom, setSummaryDateFrom] = useState(initialSummaryRange.date_from);
  const [summaryDateTo, setSummaryDateTo] = useState(initialSummaryRange.date_to);
  const [summaryPlant, setSummaryPlant] = useState('');
  const [summarySupplier, setSummarySupplier] = useState('');
  const [summaryReloadToken, setSummaryReloadToken] = useState(0);
  const [summary, setSummary] = useState<SummaryState>({ status: 'loading' });

  const summaryRangeValid = isRequestableSummaryRange(summaryDateFrom, summaryDateTo);

  useEffect(() => {
    if (tab !== 'summary' || !summaryRangeValid) {
      return;
    }
    const controller = new AbortController();
    let active = true;

    setSummary((previous) => (previous.status === 'ready' ? previous : { status: 'loading' }));

    void getReceivingExceptionSummary(
      {
        date_from: summaryDateFrom,
        date_to: summaryDateTo,
        ...(summaryPlant.trim() === '' ? {} : { plant_code: summaryPlant.trim() }),
        ...(summarySupplier.trim() === '' ? {} : { supplier_code: summarySupplier.trim() }),
      },
      { apiBaseUrl, fetchImpl, signal: controller.signal },
    ).then((result) => {
      if (!active || result.status === 'cancelled') {
        return;
      }
      setSummary(result.status === 'ready' ? { status: 'ready', summary: result.summary } : result);
    });

    return () => {
      active = false;
      controller.abort();
    };
  }, [
    tab,
    summaryRangeValid,
    summaryDateFrom,
    summaryDateTo,
    summaryPlant,
    summarySupplier,
    summaryReloadToken,
    apiBaseUrl,
    fetchImpl,
  ]);

  const applySummaryFilters = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSummaryReloadToken((token) => token + 1);
  };

  /* --------------------------------------------------------------- UI -- */

  const items = queue.status === 'ready' ? queue.items : [];

  return (
    <div className="cap-page" data-testid="receiving-exception-screen">
      <div className="cap-page__head">
        <h1 className="cap-page__title">
          ข้อยกเว้นการรับของ
          <InfoHint
            label="เกี่ยวกับหน้านี้"
            text={
              <>
                คิวที่จัดซื้อทำงาน: ของขาด/เกิน และของที่มาถึงหลัง PO ถูกปิดแบบ
                short-close แล้ว เปิดแบบไม่ปิดเป็นค่าเริ่มต้น อนุมัติเป็นเส้นเดิมของ
                receiving discrepancy ({' '}
                <code className="cap-table__code">INVENTORY</code> +{' '}
                <code className="cap-table__code">APPROVE</code>) ไม่ใช่สิทธิ์เดียวกับ
                การเปิดหน้าจอนี้
              </>
            }
          />
        </h1>
      </div>

      <div className="cap-tabs" role="tablist" data-testid="receiving-exception-tabs">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'queue'}
          className="cap-button cap-button--ghost"
          data-testid="receiving-exception-tab-queue"
          onClick={() => setTab('queue')}
        >
          คิว
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'summary'}
          className="cap-button cap-button--ghost"
          data-testid="receiving-exception-tab-summary"
          onClick={() => setTab('summary')}
        >
          รายงานสรุป
        </button>
      </div>

      {tab === 'queue' ? (
        <>
          <form
            className="cap-filters"
            data-testid="receiving-exception-filter-form"
            aria-label="ตัวกรองคิวข้อยกเว้นการรับของ"
            onSubmit={applyQueueFilters}
          >
            <label className="cap-field">
              <span className="cap-field__label">ชนิด</span>
              <select
                className="cap-input"
                data-testid="receiving-exception-filter-type"
                value={discrepancyType}
                onChange={(event) => {
                  setDiscrepancyType(event.target.value as '' | ReceivingExceptionTypeFilter);
                }}
              >
                <option value="">ทั้งหมด</option>
                {TYPE_FILTER_VALUES.map((value) => (
                  <option key={value} value={value}>
                    {discrepancyTypeLabel(value)}
                  </option>
                ))}
              </select>
            </label>

            <label className="cap-field">
              <span className="cap-field__label">ผู้ขาย</span>
              <input
                className="cap-input"
                type="search"
                data-testid="receiving-exception-filter-supplier"
                value={supplierDraft}
                onChange={(event) => {
                  setSupplierDraft(event.target.value);
                }}
                placeholder="รหัสผู้ขาย"
              />
            </label>

            <label className="cap-field">
              <span className="cap-field__label">แหล่งที่มา</span>
              <select
                className="cap-input"
                data-testid="receiving-exception-filter-source"
                value={source}
                onChange={(event) => {
                  setSource(event.target.value as '' | ReceivingExceptionSourceFilter);
                }}
              >
                <option value="">ทั้งหมด</option>
                {SOURCE_FILTER_VALUES.map((value) => (
                  <option key={value} value={value}>
                    {sourceLabel(value)}
                  </option>
                ))}
              </select>
            </label>

            <label className="cap-field">
              <span className="cap-field__label">ตั้งแต่วันที่</span>
              <input
                className="cap-input"
                type="date"
                data-testid="receiving-exception-filter-date-from"
                value={dateFromDraft}
                onChange={(event) => {
                  setDateFromDraft(event.target.value);
                }}
              />
            </label>

            <label className="cap-field">
              <span className="cap-field__label">ถึงวันที่</span>
              <input
                className="cap-input"
                type="date"
                data-testid="receiving-exception-filter-date-to"
                value={dateToDraft}
                onChange={(event) => {
                  setDateToDraft(event.target.value);
                }}
              />
            </label>

            <label className="cap-field cap-field--inline">
              <input
                type="checkbox"
                data-testid="receiving-exception-filter-include-closed"
                checked={includeClosed}
                onChange={(event) => {
                  setIncludeClosed(event.target.checked);
                }}
              />
              <span className="cap-field__label">รวมที่ปิดแล้ว</span>
            </label>

            <button
              type="submit"
              className="cap-button cap-button--ghost"
              data-testid="receiving-exception-filter-apply"
            >
              กรองรายการ
            </button>
          </form>

          {queue.status === 'loading' ? (
            <div className="cap-notice" data-testid="receiving-exception-queue-loading" role="status">
              <p className="cap-notice__text">กำลังโหลดคิว…</p>
            </div>
          ) : null}

          {queue.status !== 'loading' && queue.status !== 'ready' ? (
            <ReceivingExceptionFailureNotice
              failure={queue}
              context="queue"
              testId="receiving-exception-queue-failure"
              onRetry={() => setReloadToken((token) => token + 1)}
            />
          ) : null}

          {queue.status === 'ready' && items.length === 0 ? (
            <div className="cap-notice" data-testid="receiving-exception-queue-empty" role="status">
              <StatusBadge tone="neutral">ไม่มีรายการ</StatusBadge>
              <h2 className="cap-notice__title">ไม่มีข้อยกเว้นตามเงื่อนไขนี้</h2>
              <p className="cap-notice__text">
                เซิร์ฟเวอร์ตอบสำเร็จแต่ไม่มีแถวที่ตรงเงื่อนไข — ไม่ใช่การโหลดที่ล้มเหลว
              </p>
            </div>
          ) : null}

          {queue.status === 'ready' && items.length > 0 ? (
            <section className="cap-card cap-card--table" aria-label="คิวข้อยกเว้นการรับของ">
              <div className="cap-table-wrap">
                <table className="cap-table" data-testid="receiving-exception-queue-table">
                  <caption className="cap-visually-hidden">
                    ข้อยกเว้นการรับของที่คุณมีสิทธิ์เห็น พร้อมชนิด ปริมาณที่คาด/ที่ได้จริง
                    มูลค่า เหตุผล แหล่งที่มา และสถานะ
                  </caption>
                  <thead>
                    <tr>
                      <th scope="col">ใบสั่งซื้อ/บรรทัด</th>
                      <th scope="col">ผู้ขาย</th>
                      <th scope="col">สินค้า</th>
                      <th scope="col">ชนิด</th>
                      <th scope="col">ที่คาดไว้/ที่ได้จริง</th>
                      <th scope="col">มูลค่า</th>
                      <th scope="col">เหตุผล</th>
                      <th scope="col">แหล่งที่มา</th>
                      <th scope="col">สถานะ</th>
                      <th scope="col">การดำเนินการ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => {
                      const approveFailure = approveFailures[item.discrepancy_id] ?? null;
                      const showApprove = userCanApprove && isApprovableStatus(item.status);
                      return (
                        <tr
                          key={item.discrepancy_id}
                          data-testid={`receiving-exception-row-${item.discrepancy_id}`}
                          data-discrepancy-type={item.discrepancy_type}
                          data-status={item.status}
                        >
                          <th scope="row" className="cap-table__row-head">
                            <code className="cap-table__code">
                              {item.po_number}/{item.po_line_no}
                            </code>
                          </th>
                          <td>
                            {item.supplier_name}{' '}
                            <span className="cap-table__muted">({item.supplier_code})</span>
                          </td>
                          <td>
                            {item.product_name}{' '}
                            <span className="cap-table__muted">({item.product_code})</span>
                          </td>
                          <td>
                            <StatusBadge tone={discrepancyTypeTone(item.discrepancy_type)}>
                              {discrepancyTypeLabel(item.discrepancy_type)}
                            </StatusBadge>
                            {item.closure_reopened ? (
                              <StatusBadge
                                tone="info"
                                title="short-close ที่ทำให้เกิดแถวนี้ถูกเปิด PO กลับแล้ว — ไม่นับเป็นความผิดของผู้ขายอีกต่อไป"
                              >
                                <span
                                  data-testid={`receiving-exception-row-${item.discrepancy_id}-reopened`}
                                >
                                  เปิด PO กลับแล้ว
                                </span>
                              </StatusBadge>
                            ) : null}
                          </td>
                          <td>
                            {item.expected_qty} / {item.actual_qty}
                          </td>
                          <td>{valueLabel(item.value)}</td>
                          <td>{reasonCodeLabel(item.reason_code)}</td>
                          <td>{sourceLabel(item.source)}</td>
                          <td>
                            <StatusBadge tone={discrepancyStatusTone(item.status)}>
                              {discrepancyStatusLabel(item.status)}
                            </StatusBadge>
                          </td>
                          <td>
                            {showApprove ? (
                              <button
                                type="button"
                                className="cap-button cap-button--ghost"
                                data-testid={`receiving-exception-approve-${item.discrepancy_id}`}
                                disabled={approving !== null}
                                onClick={() => {
                                  void approve(item);
                                }}
                              >
                                {approving === item.discrepancy_id ? 'กำลังอนุมัติ…' : 'อนุมัติ'}
                              </button>
                            ) : null}
                            {approveFailure === null ? null : (
                              <div
                                data-testid={`receiving-exception-approve-failure-${item.discrepancy_id}`}
                              >
                                <ReceivingExceptionFailureNotice
                                  failure={approveFailure}
                                  context="approve"
                                  testId={`receiving-exception-approve-failure-notice-${item.discrepancy_id}`}
                                />
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="cap-card__foot">
                {queue.nextCursor === null ? (
                  <span data-testid="receiving-exception-queue-complete">
                    แสดงครบทุกรายการที่ตรงเงื่อนไขแล้ว
                  </span>
                ) : (
                  <button
                    type="button"
                    className="cap-button cap-button--ghost"
                    data-testid="receiving-exception-queue-load-more"
                    disabled={queue.loadingMore}
                    onClick={() => {
                      void loadMore();
                    }}
                  >
                    {queue.loadingMore ? 'กำลังโหลดเพิ่ม…' : 'โหลดรายการถัดไป'}
                  </button>
                )}
              </div>
            </section>
          ) : null}

          {queue.status === 'ready' && queue.loadMoreFailure !== null ? (
            <ReceivingExceptionFailureNotice
              failure={queue.loadMoreFailure}
              context="queue"
              testId="receiving-exception-queue-load-more-failure"
            />
          ) : null}
        </>
      ) : null}

      {tab === 'summary' ? (
        <>
          <form
            className="cap-filters"
            data-testid="receiving-exception-summary-form"
            aria-label="ตัวกรองรายงานสรุป"
            onSubmit={applySummaryFilters}
          >
            <label className="cap-field">
              <span className="cap-field__label">ตั้งแต่วันที่</span>
              <input
                className="cap-input"
                type="date"
                data-testid="receiving-exception-summary-date-from"
                value={summaryDateFrom}
                onChange={(event) => setSummaryDateFrom(event.target.value)}
              />
            </label>
            <label className="cap-field">
              <span className="cap-field__label">ถึงวันที่</span>
              <input
                className="cap-input"
                type="date"
                data-testid="receiving-exception-summary-date-to"
                value={summaryDateTo}
                onChange={(event) => setSummaryDateTo(event.target.value)}
              />
              <InfoHint
                label="เกี่ยวกับช่วงวันที่"
                text={`ค่าเริ่มต้นคือ 90 วันล่าสุด และอ่านได้ไม่เกิน ${SUMMARY_MAX_RANGE_DAYS} วันต่อคำขอ`}
              />
            </label>
            <label className="cap-field">
              <span className="cap-field__label">โรงงาน</span>
              <input
                className="cap-input"
                type="search"
                data-testid="receiving-exception-summary-plant"
                value={summaryPlant}
                onChange={(event) => setSummaryPlant(event.target.value)}
              />
            </label>
            <label className="cap-field">
              <span className="cap-field__label">ผู้ขาย</span>
              <input
                className="cap-input"
                type="search"
                data-testid="receiving-exception-summary-supplier"
                value={summarySupplier}
                onChange={(event) => setSummarySupplier(event.target.value)}
              />
            </label>
            <button
              type="submit"
              className="cap-button cap-button--ghost"
              data-testid="receiving-exception-summary-apply"
            >
              ดูรายงาน
            </button>
          </form>

          {!summaryRangeValid ? (
            <div
              className="cap-notice"
              data-testid="receiving-exception-summary-range-invalid"
              role="status"
            >
              <StatusBadge tone="warning">ช่วงวันที่ยังส่งคำขอไม่ได้</StatusBadge>
              <p className="cap-notice__text">
                วันสิ้นสุดต้องไม่มาก่อนวันเริ่มต้น และช่วงต้องไม่เกิน {SUMMARY_MAX_RANGE_DAYS} วัน
              </p>
            </div>
          ) : null}

          {summaryRangeValid && summary.status === 'loading' ? (
            <div className="cap-notice" data-testid="receiving-exception-summary-loading" role="status">
              <p className="cap-notice__text">กำลังโหลดรายงานสรุป…</p>
            </div>
          ) : null}

          {summaryRangeValid && summary.status !== 'loading' && summary.status !== 'ready' ? (
            <ReceivingExceptionFailureNotice
              failure={summary}
              context="summary"
              testId="receiving-exception-summary-failure"
              onRetry={() => setSummaryReloadToken((token) => token + 1)}
            />
          ) : null}

          {summaryRangeValid && summary.status === 'ready' ? (
            <section className="cap-card" aria-label="ยอดรวมของรายงานสรุป">
              <dl
                className="cap-summary-totals"
                data-testid="receiving-exception-summary-totals"
              >
                <div>
                  <dt>จำนวนข้อยกเว้น</dt>
                  <dd data-testid="receiving-exception-summary-total-exceptions">
                    {summary.summary.totals.exceptions}
                  </dd>
                </div>
                <div>
                  <dt>มูลค่าขาด</dt>
                  <dd data-testid="receiving-exception-summary-total-short-value">
                    {summary.summary.totals.short_value}
                  </dd>
                </div>
                <div>
                  <dt>มูลค่าเกิน</dt>
                  <dd data-testid="receiving-exception-summary-total-over-value">
                    {summary.summary.totals.over_value}
                  </dd>
                </div>
                <div>
                  <dt>ส่งช้าหลังปิด</dt>
                  <dd data-testid="receiving-exception-summary-total-late-deliveries">
                    {summary.summary.totals.late_deliveries}
                  </dd>
                </div>
                <div>
                  <dt>ใบสั่งซื้อที่ถูกปิดแบบขาด</dt>
                  <dd data-testid="receiving-exception-summary-total-orders-closed-short">
                    {summary.summary.totals.orders_closed_short}
                  </dd>
                </div>
              </dl>

              {summary.summary.rows.length === 0 ? (
                <p
                  className="cap-notice__text"
                  data-testid="receiving-exception-summary-rows-empty"
                >
                  ไม่มีแถวสรุปในช่วงวันที่นี้
                </p>
              ) : (
                <div className="cap-table-wrap">
                  <table className="cap-table" data-testid="receiving-exception-summary-table">
                    <caption className="cap-visually-hidden">
                      แถวสรุปตามเดือน โรงงาน ผู้ขาย ชนิด และเหตุผล
                    </caption>
                    <thead>
                      <tr>
                        <th scope="col">เดือน</th>
                        <th scope="col">โรงงาน</th>
                        <th scope="col">ผู้ขาย</th>
                        <th scope="col">ชนิด</th>
                        <th scope="col">เหตุผล</th>
                        <th scope="col">จำนวน</th>
                        <th scope="col">มูลค่า</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.summary.rows.map((row, index) => (
                        <tr
                          key={`${row.month}-${row.plant_code}-${row.supplier_code}-${row.discrepancy_type}-${row.reason_code ?? ''}-${index}`}
                          data-testid={`receiving-exception-summary-row-${index}`}
                        >
                          <td>{row.month}</td>
                          <td>
                            <code className="cap-table__code">{row.plant_code}</code>
                          </td>
                          <td>
                            {row.supplier_name}{' '}
                            <span className="cap-table__muted">({row.supplier_code})</span>
                          </td>
                          <td>
                            <StatusBadge tone={discrepancyTypeTone(row.discrepancy_type)}>
                              {discrepancyTypeLabel(row.discrepancy_type)}
                            </StatusBadge>
                          </td>
                          <td>{reasonCodeLabel(row.reason_code)}</td>
                          <td>{row.count}</td>
                          <td>{row.value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
